/**
 * Created by Sebastian on 2020-10-20.
 */

import { ArrayHelper } from "./ArrayHelper";
import { MathHelper } from "./MathHelper";
import { Helper } from './Helper';
import { GameSetting } from '../setting/GameSetting';

interface FakeReelSymbolSet {
    type: string | string[];
    amount: number | number[];
    stackedSize?: number | number[];
    maxAmountInView?: number | number[];
    excludeWithSymbol?: string[];
}

interface BaseSpinReels {
    amountOfReels: number;
    maxVisibleSize: number;
    minimumSize: number;
    fillerSymbols: FakeReelSymbolSet
}

interface IndividualSpinReels {
    name: string;
    symbols: FakeReelSymbolSet[];
    fillerReplace?:string | string[];
}

interface preBuildSpecials {
    conf: FakeReelSymbolSet;
    reelID: number;
    symbols: string[];
}

export class ReelSetBuilder {

    public static createSpinReels(base: BaseSpinReels, conf: IndividualSpinReels[]): Map<string, string[][]> {
        const res: Map<string, string[][]> = new Map();

        conf.forEach(c => { res.set(c.name, this.createFakeReels(base, c.symbols, c.fillerReplace)); })
        if(GameSetting.isDevMode) console.log(res);
        return res;
    }

    protected static createFakeReels(base: BaseSpinReels, indvi: FakeReelSymbolSet[], fillerReplaced?:string[] | string): string[][] {

        let temp: preBuildSpecials[] = [];
        const fillerBaseSet: FakeReelSymbolSet = Helper.assignObj({}, base.fillerSymbols);
        if(fillerReplaced) fillerBaseSet.type = fillerReplaced;

        // create Specials first to figure out the rules.
        for (let sP: number = 0; sP < indvi.length; sP++) {
            const fakeSym: FakeReelSymbolSet = indvi[sP];

            for (let r = 0; r < base.amountOfReels; r++) {

                const stackedValue: number = fakeSym.stackedSize != undefined ? fetchArrTypeValue(fakeSym.stackedSize!, r) : 1;
                const amm: number = fetchArrTypeValue(fakeSym.amount, r);

                for (let s: number = 0; s < (Array.isArray(fakeSym.type) ? fakeSym.type.length : 1); s++) {
                    const symType: string = fetchArrTypeValue(fakeSym.type!, r);

                    for (let a: number = 0; a < amm; a++)
                        temp.push({ conf: fakeSym, reelID: r, symbols: ArrayHelper.initArrayWithValues(stackedValue, () => symType) });
                }
            }
        }
        // shuffle them
        temp = ArrayHelper.shuffle(temp);

        // create result with some filler values.
        const res: string[][] = ArrayHelper.initArrayWithValues(base.amountOfReels, (i) => {
            const t:string[] = [];
            const startAmm:number = MathHelper.randomIntInRange(base.maxVisibleSize, base.maxVisibleSize * 2);
            for(let i:number = 0; i < startAmm;i++) addFiller(t, base.maxVisibleSize, fillerBaseSet,i)
            return t;
        });

        // how many symbols do we need to push in before the next special symbol get's added.
        let pushDistance: number = 0;
        // if there is no specials in one of the reels, add some more symbols just to make it more diverce
        const noSpecialDistance: number = 8;

        for (let l: number = 0; l < temp.length; l++) {
            const specSym: preBuildSpecials = temp[l];
            const nextSpecSym: preBuildSpecials | undefined = temp[l + 1];

            for (let r = 0; r < base.amountOfReels; r++) {
                pushDistance = MathHelper.randomIntInRange(0, base.maxVisibleSize);

                if (specSym.reelID === r && fetchArrTypeValue(specSym.conf.amount, r) != 0) {

                    // If there are to many of this symbol, push;
                    if (specSym.conf.maxAmountInView && checkRangeAmount(res[r], specSym.conf.type, base.maxVisibleSize, fetchArrTypeValue(specSym.conf.maxAmountInView, r))) {
                        addFiller(res[r],base.maxVisibleSize,fillerBaseSet,r);
                    }

                    res[r].push(...specSym.symbols);

                    if (nextSpecSym && fetchArrTypeValue(nextSpecSym.conf.amount, r) != 0) {
                        // If next symbol don't like this one, push;
                        if (nextSpecSym.conf.excludeWithSymbol && nextSpecSym.conf.excludeWithSymbol.indexOf(fetchArrTypeValue(specSym.conf.type!, r)) >= 0) {
                            if (pushDistance < base.maxVisibleSize) pushDistance = base.maxVisibleSize;
                        }
                    }

                } else {
                    pushDistance = noSpecialDistance;
                }

                for (let k: number = 0; k < pushDistance; k++) {
                    addFiller(res[r],base.maxVisibleSize,fillerBaseSet,r);
                }
            }
        }

        for(let mini:number = 0; mini < res.length;mini++){
            while(res[mini].length < base.minimumSize){
                res[mini].push(fillerBaseSet.type[MathHelper.randomIntInRange(0, fillerBaseSet.type.length - 1)])
            }
        }

        return res;

        function addFiller(arr: string[], maxVisibleSize: number, fillerSyms: FakeReelSymbolSet, reel: number){
            if (Array.isArray(fillerSyms.type)) {
                if (fillerSyms.maxAmountInView) {
                    arr.push(findNewFiller(arr, maxVisibleSize, fillerSyms, reel))
                } else {
                    arr.push(fillerSyms.type[MathHelper.randomIntInRange(0, fillerSyms.type.length - 1)])
                }
            }else{
                arr.push(fillerSyms.type);
            }
        }

        function findNewFiller(arr: string[], maxVisibleSize: number, fillerSyms: FakeReelSymbolSet, reel: number) {

            const isArray: boolean = Array.isArray(fillerSyms.type);
            let breaker: number = 100;
            let indx: number = isArray ? MathHelper.randomIntInRange(0, fillerSyms.type.length - 1) : 0;
            let symToAdd: string = "";

           while (true){
                symToAdd = isArray ? fillerSyms.type[indx] : fillerSyms.type as string;
                indx = (indx + 1) % (isArray ? fillerSyms.type.length : 0);

                // If it couldn't find a new sym, return the last symbol.
                breaker--;
                if (breaker <= 0) break;
                if(!checkRangeAmount(arr, symToAdd, maxVisibleSize, fetchArrTypeValue(fillerSyms.maxAmountInView, reel))) break;
            }

            return symToAdd;
        }

        function fetchArrTypeValue(arr: any | any[], indx: number): any {
            return (Array.isArray(arr) ? (arr as any[])[indx] : arr as any);
        }

        function checkRangeAmount(arr: string[], type: string | string[], range: number, amount: number | undefined): boolean {
            if (amount === undefined) return false;

            const isArr: boolean = Array.isArray(type);
            const startPos: number = MathHelper.constrain(arr.length - range, 0, arr.length - 1);
            let counter: number = 0;

            for (let k: number = startPos; k < arr.length; k++) {
                if (isArr && (type as string[]).indexOf(arr[k]) >= 0) {
                    counter++;
                } else if (type === arr[k]) {
                    counter++;
                }

                if (counter >= amount) return true;
            }

            return false;
        }
    }
}
